import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Download } from "lucide-react";

interface BinderEvidence {
  link_id: string;
  source_type: string;
  source_id: string;
  status: string;
  effective_from: string;
  effective_to: string | null;
  source: any;
}

interface BinderSection {
  control_id: string;
  clause_code: string;
  title: string;
  description: string | null;
  criticality: string;
  evidence_count: number;
  valid_count: number;
  expiring_count: number;
  expired_count: number;
  status: string;
  evidence: BinderEvidence[];
  narrative: string;
}

export interface AuditBinder {
  binder_id: string;
  generated_at: string;
  generated_by_user_id: string;
  lang: "id" | "en";
  cover: {
    company: any;
    site: any;
    framework: any;
    period_start: string | null;
    period_end: string | null;
    readiness_score: number;
  };
  summary: { total_controls: number; compliant: number; partial: number; at_risk: number; gap: number };
  sections: BinderSection[];
  gap_register: { clause_code: string; title: string; status: string; criticality: string }[];
  chain_of_custody: { control: string; source_type: string; source_id: string; file_url: string; captured_at: string; sha256_url_ref: string }[];
}

const STATUS_COLOR: Record<string, string> = {
  compliant: "bg-emerald-100 text-emerald-700 border-emerald-300",
  partial: "bg-amber-100 text-amber-700 border-amber-300",
  at_risk: "bg-rose-100 text-rose-700 border-rose-300",
  gap: "bg-slate-100 text-slate-600 border-slate-300",
};

function evidenceLabel(e: BinderEvidence): string {
  const s = e.source || {};
  return s.finding_text || s.hazard_description || s.description || s.ai_summary || s.title || s.file_name || `${e.source_type} #${e.source_id.slice(0, 8)}`;
}
function evidencePhoto(e: BinderEvidence): string | null {
  return e.source?.photo_url || e.source?.file_url || null;
}

function downloadJson(binder: AuditBinder) {
  const blob = new Blob([JSON.stringify(binder, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-binder-${binder.cover.framework?.code || "framework"}-${binder.binder_id.slice(0, 8)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

const AuditBinderDialog = ({ binder, open, onClose }: { binder: AuditBinder | null; open: boolean; onClose: () => void }) => {
  if (!binder) return null;
  const cov = binder.cover;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full print:overflow-visible">
        <DialogHeader className="print:hidden">
          <DialogTitle className="flex items-center justify-between">
            <span>Audit Binder Preview</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadJson(binder)}>
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
              <Button size="sm" onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div id="audit-binder-print" className="space-y-6 text-sm">
          {/* COVER */}
          <section className="border rounded-lg p-6 bg-card">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Audit Binder</div>
            <h1 className="text-2xl font-bold mt-1">{cov.framework?.name_id || cov.framework?.name}</h1>
            <div className="text-muted-foreground">{cov.framework?.code} • {cov.framework?.version} • {cov.framework?.jurisdiction}</div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <div className="text-xs text-muted-foreground">Perusahaan</div>
                <div className="font-semibold">{cov.company?.company_name}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Site / Lokasi</div>
                <div className="font-semibold">{cov.site?.location_name || "All sites"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Periode</div>
                <div className="font-semibold">{cov.period_start || "—"} → {cov.period_end || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Audit Readiness Score</div>
                <div className="text-3xl font-bold text-primary">{cov.readiness_score}%</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-4 gap-2">
              <div className="border rounded p-2 text-center">
                <div className="text-xs text-muted-foreground">Compliant</div>
                <div className="text-lg font-bold text-emerald-600">{binder.summary.compliant}</div>
              </div>
              <div className="border rounded p-2 text-center">
                <div className="text-xs text-muted-foreground">Partial</div>
                <div className="text-lg font-bold text-amber-600">{binder.summary.partial}</div>
              </div>
              <div className="border rounded p-2 text-center">
                <div className="text-xs text-muted-foreground">At Risk</div>
                <div className="text-lg font-bold text-rose-600">{binder.summary.at_risk}</div>
              </div>
              <div className="border rounded p-2 text-center">
                <div className="text-xs text-muted-foreground">Gap</div>
                <div className="text-lg font-bold text-slate-500">{binder.summary.gap}</div>
              </div>
            </div>
            <div className="mt-6 text-xs text-muted-foreground">
              Generated {new Date(binder.generated_at).toLocaleString()} • Binder ID {binder.binder_id}
            </div>
          </section>

          {/* SECTIONS */}
          {binder.sections.map((s) => (
            <section key={s.control_id} className="border rounded-lg p-4 bg-card break-inside-avoid">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-mono text-xs text-muted-foreground">{s.clause_code}</div>
                  <h3 className="font-semibold">{s.title}</h3>
                  {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
                </div>
                <Badge variant="outline" className={STATUS_COLOR[s.status]}>{s.status}</Badge>
              </div>

              {s.narrative && (
                <div className="mt-3 p-3 rounded bg-muted/40 text-sm italic">
                  "{s.narrative}"
                </div>
              )}

              <div className="mt-3">
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  Bukti ({s.valid_count} valid / {s.evidence_count} total)
                </div>
                {s.evidence.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">Tidak ada bukti tertaut.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {s.evidence.slice(0, 9).map((e) => {
                      const photo = evidencePhoto(e);
                      return (
                        <div key={e.link_id} className="border rounded p-2 text-xs">
                          {photo && (
                            <img src={photo} alt="" className="w-full h-24 object-cover rounded mb-1" />
                          )}
                          <div className="font-medium line-clamp-2">{evidenceLabel(e)}</div>
                          <div className="text-muted-foreground mt-1">
                            {e.source_type} • {e.status}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          ))}

          {/* GAP REGISTER */}
          {binder.gap_register.length > 0 && (
            <section className="border rounded-lg p-4 bg-card break-before-page">
              <h3 className="font-semibold mb-3">Gap Register</h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Klausul</th>
                    <th className="text-left">Kontrol</th>
                    <th className="text-left">Kritikalitas</th>
                    <th className="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {binder.gap_register.map((g) => (
                    <tr key={g.clause_code} className="border-b">
                      <td className="font-mono py-1">{g.clause_code}</td>
                      <td>{g.title}</td>
                      <td>{g.criticality}</td>
                      <td>{g.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* CHAIN OF CUSTODY */}
          {binder.chain_of_custody.length > 0 && (
            <section className="border rounded-lg p-4 bg-card break-before-page">
              <h3 className="font-semibold mb-3">Chain of Custody Manifest</h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Klausul</th>
                    <th className="text-left">Sumber</th>
                    <th className="text-left">Captured</th>
                    <th className="text-left">SHA-256 (url+ts)</th>
                  </tr>
                </thead>
                <tbody>
                  {binder.chain_of_custody.map((m, i) => (
                    <tr key={i} className="border-b">
                      <td className="font-mono py-1">{m.control}</td>
                      <td>{m.source_type}</td>
                      <td>{m.captured_at ? new Date(m.captured_at).toLocaleString() : "—"}</td>
                      <td className="font-mono break-all">{m.sha256_url_ref.slice(0, 24)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuditBinderDialog;
